import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios'
import {
  ApiError,
  ApiResponse,
  PaginatedResponse,
  ApiClientConfig,
  PaginationRequest,
  AuthenticationResult,
} from '../types'
import { tokenUtils, toAuthTokens, AUTH_EXPIRED_EVENT } from '../utils/auth.utils'
import { retryWithBackoff } from '../utils/error.utils'

/** Default API root when `VITE_API_URL` is not set (matches the .NET dev profile). */
export const DEFAULT_API_URL = 'http://localhost:5018/api'

/** Endpoints that must never trigger a token refresh on 401. */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh', '/auth/register', '/auth/microsoft']

/**
 * Wrap a raw API body in the `{ data, success }` envelope the services expect.
 * The .NET API returns bare payloads (`Ok(dto)`), so this keeps callers uniform.
 */
export const toApiResponse = <T>(body: unknown): ApiResponse<T> => {
  if (
    body !== null &&
    typeof body === 'object' &&
    'data' in body &&
    'success' in body &&
    typeof (body as ApiResponse<T>).success === 'boolean'
  ) {
    return body as ApiResponse<T>
  }
  return { data: body as T, success: true }
}

/**
 * Normalise the list shapes the API can return (OData `{ value }`, a bare array,
 * or an already-paged `{ items }`) into `PaginatedResponse`.
 */
export const toPaginatedResponse = <T>(
  body: unknown,
  pagination?: PaginationRequest
): PaginatedResponse<T> => {
  const pageNumber = pagination?.pageNumber ?? 1
  const requestedSize = pagination?.pageSize

  let items: T[] = []
  let totalCount: number | undefined

  if (Array.isArray(body)) {
    items = body as T[]
  } else if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>
    if (Array.isArray(record.items)) {
      return record as unknown as PaginatedResponse<T>
    }
    if (Array.isArray(record.value)) {
      items = record.value as T[]
      const count = record['@odata.count']
      if (typeof count === 'number') totalCount = count
    }
  }

  const pageSize = requestedSize ?? (items.length || 1)
  const total = totalCount ?? items.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return {
    items,
    totalCount: total,
    pageNumber,
    pageSize,
    totalPages,
    hasNextPage: pageNumber < totalPages,
    hasPreviousPage: pageNumber > 1,
  }
}

/** Build an OData query string from pagination options and an optional `$filter`. */
export const buildODataQuery = (
  pagination?: PaginationRequest,
  filter?: string,
  extra?: Record<string, string | number | boolean | undefined>
): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = { $count: true }
  if (pagination?.pageSize) {
    params.$top = pagination.pageSize
    if (pagination.pageNumber && pagination.pageNumber > 1) {
      params.$skip = (pagination.pageNumber - 1) * pagination.pageSize
    }
  }
  if (pagination?.sortBy) {
    params.$orderby = `${pagination.sortBy} ${pagination.sortDirection ?? 'asc'}`
  }
  if (filter) params.$filter = filter
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      if (value !== undefined) params[key] = value
    }
  }
  return params
}

/** Escape a string literal for use inside an OData `$filter`. */
export const odataString = (value: string): string => `'${value.replace(/'/g, "''")}'`

export class ApiService {
  private client: AxiosInstance
  private refreshPromise: Promise<void> | null = null
  private readonly baseURL: string

  constructor(config: ApiClientConfig) {
    this.baseURL = config.baseURL.replace(/\/+$/, '')
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    })

    this.setupInterceptors()
  }

  /** The API root, e.g. `https://api.templatean.com/api`. */
  getBaseURL(): string {
    return this.baseURL
  }

  /** The API origin without the `/api` suffix, for resolving relative file URLs. */
  getOrigin(): string {
    try {
      return new URL(this.baseURL).origin
    } catch {
      return this.baseURL.replace(/\/api$/, '')
    }
  }

  /** Resolve a possibly relative URL (e.g. `/uploads/x.png`) against the API origin. */
  resolveUrl(url: string | null | undefined): string {
    if (!url) return ''
    if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) {
      return url
    }
    return `${this.getOrigin()}${url.startsWith('/') ? '' : '/'}${url}`
  }

  private isAuthEndpoint(url?: string): boolean {
    if (!url) return false
    return AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint))
  }

  private setupInterceptors() {
    // Request interceptor for auth
    this.client.interceptors.request.use(
      (config) => {
        const authHeader = tokenUtils.getAuthHeader()
        if (authHeader) {
          config.headers.Authorization = authHeader
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

        // A 401 means the token has expired. A 403 can mean the same thing in a
        // subtler way: permissions are written into the token when it is issued, so a
        // session that started before an administrator granted access keeps being
        // refused even though the account now has it. Refreshing mints a token with
        // current permissions, so both are worth one retry.
        const refreshable = error.response?.status === 401 || error.response?.status === 403

        if (
          refreshable &&
          originalRequest &&
          !originalRequest._retry &&
          !this.isAuthEndpoint(originalRequest.url)
        ) {
          originalRequest._retry = true

          if (!this.refreshPromise) {
            this.refreshPromise = this.refreshAccessToken().finally(() => {
              this.refreshPromise = null
            })
          }

          try {
            await this.refreshPromise
            return this.client(originalRequest)
          } catch (refreshError) {
            // A refusal the refresh cannot fix is a genuine permission problem, not an
            // expired session, so do not sign the user out over it.
            if (error.response?.status === 403) {
              return Promise.reject(this.handleError(error as AxiosError<ApiError>))
            }
            this.handleSessionExpired()
            return Promise.reject(this.handleError(error as AxiosError<ApiError>))
          }
        }

        return Promise.reject(this.handleError(error as AxiosError<ApiError>))
      }
    )
  }

  /**
   * Clear the session and notify the UI. Inside an Office task pane there is no
   * `/login` route to navigate to, so we dispatch an event that `useAuth` listens for.
   */
  private handleSessionExpired(): void {
    tokenUtils.clearTokens()
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
    }
  }

  private async refreshAccessToken(): Promise<void> {
    const tokens = tokenUtils.getTokens()
    if (!tokens?.refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await this.client.post<AuthenticationResult>('/v1/auth/refresh', {
      refreshToken: tokens.refreshToken,
    })

    tokenUtils.setTokens(toAuthTokens(response.data))
  }

  private handleError(error: AxiosError<ApiError>): ApiError {
    const data = error.response?.data as unknown
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>
      const message =
        (typeof record.message === 'string' && record.message) ||
        (typeof record.title === 'string' && record.title) ||
        (typeof record.errorMessage === 'string' && record.errorMessage) ||
        error.message ||
        'Request failed'
      return {
        message,
        code: (typeof record.code === 'string' && record.code) || `HTTP_${error.response?.status ?? 'ERROR'}`,
        details: record,
        timestamp: new Date().toISOString(),
      }
    }
    if (typeof data === 'string' && data.trim()) {
      return {
        message: data,
        code: `HTTP_${error.response?.status ?? 'ERROR'}`,
        timestamp: new Date().toISOString(),
      }
    }

    return {
      message: error.message || 'An unexpected error occurred',
      code: 'NETWORK_ERROR',
      timestamp: new Date().toISOString(),
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get<unknown>(url, config)
    return toApiResponse<T>(response.data)
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post<unknown>(url, data, config)
    return toApiResponse<T>(response.data)
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put<unknown>(url, data, config)
    return toApiResponse<T>(response.data)
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<unknown>(url, config)
    return toApiResponse<T>(response.data)
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch<unknown>(url, data, config)
    return toApiResponse<T>(response.data)
  }

  /**
   * GET a list endpoint and normalise the result to `PaginatedResponse`.
   * Pass OData params (`$top`, `$filter`, ...) via `params`; see `buildODataQuery`.
   */
  async getPaginated<T>(
    url: string,
    params?: Record<string, any>,
    pagination?: PaginationRequest
  ): Promise<PaginatedResponse<T>> {
    const response = await this.client.get<unknown>(url, { params })
    return toPaginatedResponse<T>(response.data, pagination)
  }

  async upload<T>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.client.post<unknown>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        if (event.total && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress)
        }
      },
    })

    return toApiResponse<T>(response.data)
  }

  async withRetry<T>(
    request: () => Promise<T>,
    options?: Parameters<typeof retryWithBackoff>[1]
  ): Promise<T> {
    return retryWithBackoff(request, options)
  }
}

// Create a singleton instance
export const apiService = new ApiService({
  baseURL: import.meta.env.VITE_API_URL || DEFAULT_API_URL,
})
